import React, { useEffect, useState, useCallback } from "react";
import { observer, inject } from "mobx-react";
import { useNavigate } from "react-router-dom";
import { fetch } from "../utils/api-wrapper";
import DataStore from "../utils/data-store";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

/**
 * ============================================================
 * SettingsPage
 * ============================================================
 *
 * This page:
 *  - Displays the user's general information (first name, last name, unit)
 *  - Lets the user change their password
 *  - Shows a live "password compliance" checklist (green when satisfied)
 *  - Shows a single, focused error message when submission fails
 *  - Works in multiple languages (EN/FR):
 *      - Labels update when language changes
 *      - Errors update when language changes (because we store error *keys*)
 *  - Allows users to toggle password visibility (eye icon)
 *
 * Architectural notes:
 *  - The component is exported as inject("data")(observer(SettingsPage)).
 *  - `data` is a MobX store injected via <Provider data={...}>.
 *  - Because it is `observer`, it re-renders when observable values it reads
 *    during render change (ex: current language).
 */

// ------------------------------------------------------------
// Password rule keys used by the compliance list UI
// ------------------------------------------------------------

const lowerAndUpperCase = "LowerAndUpperCase";
const digitalNumber = "DigitalNumber";
const specialChar = "SpecialChar";
const minMaxChar = "MinMaxChar";
const isIdentical = "IsIdentical";

// ------------------------------------------------------------
// Regex patterns
// ------------------------------------------------------------

const regexCaseLowUpp = /(?=.*[a-z])(?=.*[A-Z])/;
const regexContainNumber = /(?=.*[\d])/;
const regexSpecialCharacter = /(?=.*?[!$"%?&*().,\-=_+])/;

const regexLowercase = /[a-z]/;
const regexUppercase = /[A-Z]/;

// ------------------------------------------------------------
// Pure helper functions
// ------------------------------------------------------------

function computePasswordValidity(newPassword, newPasswordConfirm) {
  return {
    [minMaxChar]: newPassword.length >= 10 && newPassword.length <= 64,
    [lowerAndUpperCase]: regexCaseLowUpp.test(newPassword),
    [digitalNumber]: regexContainNumber.test(newPassword),
    [specialChar]: regexSpecialCharacter.test(newPassword),
    [isIdentical]:
      newPassword === newPasswordConfirm &&
      newPasswordConfirm.trim() !== "" &&
      newPassword.trim() !== "",
  };
}

function getPasswordErrorKey(newPassword, newPasswordConfirm) {
  if (
    newPassword.trim() !== "" &&
    newPasswordConfirm.trim() !== "" &&
    newPassword !== newPasswordConfirm
  ) {
    return "errors.new_password_confirmation_mismatch";
  }

  if (newPassword.length < 10) return "errors.new_password_too_short";
  if (newPassword.length > 64) return "errors.new_password_too_long";

  if (!regexUppercase.test(newPassword))
    return "errors.new_password_missing_uppercase";
  if (!regexLowercase.test(newPassword))
    return "errors.new_password_missing_lowercase";
  if (!regexContainNumber.test(newPassword))
    return "errors.new_password_missing_number";
  if (!regexSpecialCharacter.test(newPassword))
    return "errors.new_password_missing_special_char";

  return null;
}

// ------------------------------------------------------------
// Main component
// ------------------------------------------------------------

function SettingsPage({ data }) {
  const navigate = useNavigate();

  const currentLang = data.t.getCurrentLang();

  // --------------------
  // Form state
  // --------------------

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");

  // ------------------------------------------------------------
  // Password visibility state (NEW)
  // ------------------------------------------------------------
  // Each password field has its own visibility toggle.
  // We intentionally keep them separate so users can
  // control visibility independently for each input.

  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);

  // ------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------
  // Error key is used to display the error message from the API response.
  // Success message is used to display the success message after the password is updated.
  // Default language is used to display the correct language for the error and success messages.
  const [errorKey, setErrorKey] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [defaultLang, setDefaultLang] = useState(currentLang);

  // --------------------
  // Compliance checklist state
  // --------------------

  const [passwordCondition, setPasswordCondition] = useState({
    [lowerAndUpperCase]: {
      label: data.t.read("password_compliance.low_up_case"),
      isValid: false,
    },
    [digitalNumber]: {
      label: data.t.read("password_compliance.digit_number"),
      isValid: false,
    },
    [specialChar]: {
      label: data.t.read("password_compliance.special_char"),
      isValid: false,
    },
    [minMaxChar]: {
      label: data.t.read("password_compliance.min_max_char"),
      isValid: false,
    },
    [isIdentical]: {
      label: data.t.read("password_compliance.is_identical"),
      isValid: false,
    },
  });

  // ------------------------------------------------------------
  // Translation helpers for errors
  // ------------------------------------------------------------

  const readError = useCallback(
    (key) => {
      if (!key) return null;

      const direct = data.t.read(key);
      if (typeof direct === "string" && direct.trim() !== "") return direct;

      if (key.startsWith("errors.")) {
        const leaf = key.replace("errors.", "");
        const errorsObj = data.t.read("errors");

        if (errorsObj && typeof errorsObj === "object") {
          const nested = errorsObj[leaf];
          if (typeof nested === "string" && nested.trim() !== "") return nested;
        }

        const noPrefix = data.t.read(leaf);
        if (typeof noPrefix === "string" && noPrefix.trim() !== "")
          return noPrefix;
      }

      return null;
    },
    [data]
  );

  const setErrorFromKey = useCallback((key) => {
    setErrorKey(key || "errors.unknown_error");
  }, []);

  // ------------------------------------------------------------
  // Effects
  // ------------------------------------------------------------

  useEffect(() => {
    const validity = computePasswordValidity(newPassword, newPasswordConfirm);

    setPasswordCondition((prev) => ({
      ...prev,
      [minMaxChar]: { ...prev[minMaxChar], isValid: validity[minMaxChar] },
      [lowerAndUpperCase]: {
        ...prev[lowerAndUpperCase],
        isValid: validity[lowerAndUpperCase],
      },
      [digitalNumber]: {
        ...prev[digitalNumber],
        isValid: validity[digitalNumber],
      },
      [specialChar]: { ...prev[specialChar], isValid: validity[specialChar] },
      [isIdentical]: { ...prev[isIdentical], isValid: validity[isIdentical] },
    }));
  }, [newPassword, newPasswordConfirm]);

  useEffect(() => {
    if (errorKey) setErrorKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oldPassword, newPassword, newPasswordConfirm]);

  useEffect(() => {
    if (currentLang !== defaultLang) {
      setDefaultLang(currentLang);

      setPasswordCondition((prev) => ({
        [lowerAndUpperCase]: {
          label: data.t.read("password_compliance.low_up_case"),
          isValid: prev[lowerAndUpperCase].isValid,
        },
        [digitalNumber]: {
          label: data.t.read("password_compliance.digit_number"),
          isValid: prev[digitalNumber].isValid,
        },
        [specialChar]: {
          label: data.t.read("password_compliance.special_char"),
          isValid: prev[specialChar].isValid,
        },
        [minMaxChar]: {
          label: data.t.read("password_compliance.min_max_char"),
          isValid: prev[minMaxChar].isValid,
        },
        [isIdentical]: {
          label: data.t.read("password_compliance.is_identical"),
          isValid: prev[isIdentical].isValid,
        },
      }));
    }
  }, [currentLang, defaultLang, data]);

  // ------------------------------------------------------------
  // Actions
  // ------------------------------------------------------------

  const refreshTokenAndGoToDashboard = useCallback(() => {
    fetch(
      "/refresh-token",
      "GET",
      {},
      (response) => {
        DataStore.updateUserData(data, response.data, response.data.token);
        data.password_expired = false;
        navigate("/dashboard");
      }
    );
  }, [data, navigate]);

  const updatePassword = useCallback(() => {
    setErrorKey(null);

    const form = new FormData();
    form.append("oldPassword", oldPassword);
    form.append("newPassword", newPassword);

    fetch(
      "/auth/update-password",
      "POST",
      form,
      () => {
        refreshTokenAndGoToDashboard();
      },
      (error) => {
        const apiKey = error?.response?.data;

        if (apiKey === "invalid_credentials") {
          setErrorFromKey("errors.old_password_invalid");
          return;
        }

        const fallbackKey = apiKey
          ? `errors.${apiKey}`
          : "errors.unknown_error";
        setErrorFromKey(fallbackKey);
      }
    );
  }, [oldPassword, newPassword, setErrorFromKey, refreshTokenAndGoToDashboard]);

  const handleUpdatePasswordSubmit = useCallback(
    (event) => {
      event.preventDefault();

      setSuccessMessage(null);

      const validationErrorKey = getPasswordErrorKey(
        newPassword,
        newPasswordConfirm
      );

      if (validationErrorKey) {
        setErrorFromKey(validationErrorKey);
        return;
      }

      updatePassword();
    },
    [newPassword, newPasswordConfirm, updatePassword, setErrorFromKey]
  );

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <div className="container">
      <div className="row">
        <h3 className="font-weight-bold">{data.t.read("settings")}</h3>
      </div>

      <div className="row">
        <div className="col-md-6 mw-500px">
          <h4 className="mt-4 mb-3">{data.t.read("general_info")}</h4>

          <div className="row">
            <div className="col-md-4">{data.t.read("first_name")} :</div>
            <div className="col-md-8">{data.firstName}</div>
          </div>

          <div className="row">
            <div className="col-md-4">{data.t.read("last_name")} :</div>
            <div className="col-md-8">{data.lastName}</div>
          </div>

          <div className="row">
            <div className="col-md-4">{data.t.read("unit")} :</div>
            <div className="col-md-8">{data.unit}</div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mw-400px mt-5 mb-3">
          <h4>{data.t.read("setting_password.change_password")}</h4>

          <div className="alert-section">
            {errorKey != null ? (
              <div className="alert alert-danger" role="alert">
                {readError(errorKey) ||
                  readError("errors.unknown_error") ||
                  "Error"}
              </div>
            ) : successMessage != null ? (
              <div className="alert alert-success" role="alert">
                {successMessage}
              </div>
            ) : null}
          </div>

          <form onSubmit={handleUpdatePasswordSubmit} autoComplete="off">
            {/* Old password input with visibility toggle */}
            <div className="form-group input-group-sm position-relative">
              <input
                className="form-control pr-5"
                placeholder={data.t.read("setting_password.old_password")}
                required
                type={showOldPassword ? "text" : "password"}
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
              />
              <span
                onClick={() => setShowOldPassword((v) => !v)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  padding: "8px",
                }}
                aria-label="Toggle old password visibility"
              >
                <FontAwesomeIcon
                  icon={showOldPassword ? faEyeSlash : faEye}
                />
              </span>
            </div>

            {/* New password input with visibility toggle */}
            <div className="form-group input-group-sm mt-4 position-relative">
              <input
                className="form-control pr-5"
                placeholder={data.t.read("setting_password.new_password")}
                required
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <span
                onClick={() => setShowNewPassword((v) => !v)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  padding: "8px",
                }}
                aria-label="Toggle new password visibility"
              >
                <FontAwesomeIcon
                  icon={showNewPassword ? faEyeSlash : faEye}
                />
              </span>
            </div>

            {/* New password confirmation input with visibility toggle */}
            <div className="form-group input-group-sm position-relative">
              <input
                className="form-control pr-5"
                placeholder={data.t.read(
                  "setting_password.new_password_confirm"
                )}
                required
                type={showNewPasswordConfirm ? "text" : "password"}
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
              />
              <span
                onClick={() => setShowNewPasswordConfirm((v) => !v)}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  cursor: "pointer",
                  padding: "8px",
                }}
                aria-label="Toggle password confirmation visibility"
              >
                <FontAwesomeIcon
                  icon={showNewPasswordConfirm ? faEyeSlash : faEye}
                />
              </span>
            </div>

            <div>
              <h5>{data.t.read("password_compliance.description")}</h5>
              <ul>
                {Object.entries(passwordCondition).map(([key, value]) => (
                  <li
                    key={key}
                    className={
                      value.isValid
                        ? "password-check-green"
                        : "password-check-default"
                    }
                  >
                    {value.label}
                  </li>
                ))}
              </ul>
            </div>

            <div className="form-group">
              <button className="btn btn-block btn-primary">
                {data.t.read("setting_password.update_password")}
              </button>
            </div>
          </form>
        </div>

        <div className="col-md-6 mt-5 mb-3">
          <ul>
            {data.t.read("password_compliance.indications").map((item, i) => (
              <li key={i} className="settings-conformity-text">
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export default inject("data")(observer(SettingsPage));